/** Server API + auth. Logto in production; a dev token when the server exposes /api/dev/token. */

import LogtoClient from "@logto/browser";

// The resource indicator must match the server's LOGTO_AUDIENCE exactly, or every call 401s
// with a token that looks valid. A fork points both at its own API resource.
export const LOGTO = {
  endpoint: "https://auth.cambion.lol",
  appId: (import.meta.env.VITE_LOGTO_APP_ID as string) ?? "",
  resource: (import.meta.env.VITE_LOGTO_RESOURCE as string) || "https://launch.cambion.lol/api",
  scopes: ["game:play"],
};

const DEV_KEY = "launch:devToken";
let devToken: string | null = localStorage.getItem(DEV_KEY);
let logto: LogtoClient | null = null;

export function isDevMode() {
  return !!devToken || location.hostname === "localhost" || location.hostname === "127.0.0.1";
}

export async function devLogin(sub: string) {
  const r = await fetch("/api/dev/token", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ sub }) });
  if (!r.ok) throw new Error("dev tokens disabled on this server");
  devToken = (await r.json()).token;
  localStorage.setItem(DEV_KEY, devToken!);
}

export function devLogout() {
  devToken = null;
  localStorage.removeItem(DEV_KEY);
}

function client() {
  if (!logto) logto = new LogtoClient({ endpoint: LOGTO.endpoint, appId: LOGTO.appId, resources: [LOGTO.resource], scopes: LOGTO.scopes });
  return logto;
}

export async function token(): Promise<string | null> {
  const t = await rawToken();
  // hand the token to the native shell so the Travel service can post positions without the UI
  const n = native();
  if (n && t !== lastPushedToken) { try { n.setToken(t ?? ""); lastPushedToken = t; } catch { /* old shell */ } }
  return t;
}
async function rawToken(): Promise<string | null> {
  if (devToken) return devToken;
  if (!LOGTO.appId) return null;
  const c = client();
  if (!(await c.isAuthenticated())) return null;
  try { return await c.getAccessToken(LOGTO.resource); } catch { return null; }
}

export async function signIn() {
  await client().signIn(`${location.origin}/callback`);
}
export async function handleCallback() {
  if (location.pathname === "/callback") {
    await client().handleSignInCallback(location.href);
    history.replaceState({}, "", "/");
  }
}
export async function signOut() {
  if (devToken) { devLogout(); location.reload(); return; }
  await client().signOut(`${location.origin}/`);
}

/** The Android shell injects window.Launch; then we are the "android" client and the phone owns location. */
export const native = (): any | null => (globalThis as any).Launch ?? null;
const clientKind = () => (native() ? "android" : "browser");
let lastPushedToken: string | null = null;

export async function api<T = any>(path: string, body?: unknown): Promise<T> {
  const t = await token();
  const r = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    headers: { "content-type": "application/json", ...(t ? { authorization: `Bearer ${t}` } : {}), "x-launch-client": clientKind() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error ?? j.reason ?? `${r.status}`);
  return j as T;
}

export const command = (command: string, args: Record<string, unknown> = {}) => api<{ ok: boolean; reason?: string; me: any }>("/api/command", { command, args });
